import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    createTheme,
    CssBaseline,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    LinearProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Snackbar,
    TextField,
    ThemeProvider,
    Tooltip,
    Typography
} from '@mui/material';
import {
    Assignment as AssignmentIcon,
    Business as FacilityIcon,
    Code as CodeIcon,
    ContentCopy as CopyIcon,
    DarkMode as DarkModeIcon,
    Image as ImageIcon,
    LibraryBooks as LibraryIcon,
    LightMode as LightModeIcon,
    Message as MessageIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    MoreVert as MoreIcon,
    Person as PersonIcon,
    Receipt as ReceiptIcon,
    Refresh as RegenerateIcon,
    School as SchoolIcon,
    Send as SendIcon,
    Settings as SettingsIcon,
    SmartToy as BotIcon,
    Stop as StopIcon,
    ThumbDown as ThumbDownIcon,
    ThumbUp as ThumbUpIcon,
    Upload as UploadIcon,
    VolumeOff as MuteIcon,
    VolumeUp as SpeakIcon
} from '@mui/icons-material';
import {api} from "../../../common";
import {
    ActionButton,
    ActionChip,
    ButtonContainer,
    ChatContainer,
    FileUploadArea,
    InputContainer,
    InputWrapper,
    MainContent,
    MessageActions,
    MessageBubble,
    MessageContent,
    MessageHeader,
    MessagesContainer,
    ModeChip,
    ModeSelector,
    ModernAvatar,
    RecordingDialog,
    StyledTextField,
    theme,
    TypingIndicator,
    WaveBar,
    WaveformContainer,
    WelcomeCard,
    WelcomeIcon
} from './AIChat.styles';

// Modern Discord/Slack inspired theme

const AIChat = () => {
    // Basic state
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [context, setContext] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedMode, setSelectedMode] = useState('chat');
    const [copySuccess, setCopySuccess] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null); // Added for menu handling

    // Theme state
    const [darkMode, setDarkMode] = useState(false);
    const [showSpeechSettings, setShowSpeechSettings] = useState(false);

    // Speech synthesis state
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [speechRate, setSpeechRate] = useState(1);
    const [speechPitch, setSpeechPitch] = useState(1);
    const [speechVoice, setSpeechVoice] = useState(null);
    const [availableVoices, setAvailableVoices] = useState([]);

    // Audio recording state
    const [isRecording, setIsRecording] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const [audioLevel, setAudioLevel] = useState(0);
    const [showRecordingDialog, setShowRecordingDialog] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [showFileUpload, setShowFileUpload] = useState(false);

    // Image handling state
    const [showImageUpload, setShowImageUpload] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const [imageAnalysisPrompt, setImageAnalysisPrompt] = useState('');
    const [imageUploadMode, setImageUploadMode] = useState('multimodal');
    const [selectedSpecialization, setSelectedSpecialization] = useState(null);
    const [isImageAnalyzing, setIsImageAnalyzing] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);

    // Refs
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const streamRef = useRef(null);
    const analyserRef = useRef(null);
    const recordingIntervalRef = useRef(null);
    const audioContextRef = useRef(null);
    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Initialize speech synthesis and load available voices
    useEffect(() => {
        // Check if browser supports speech synthesis
        if ('speechSynthesis' in window) {
            // Get available voices
            const loadVoices = () => {
                const availableSynthesisVoices = window.speechSynthesis.getVoices();
                if (availableSynthesisVoices.length > 0) {
                    setAvailableVoices(availableSynthesisVoices);
                    // Set default voice (prefer English)
                    const defaultVoice = availableSynthesisVoices.find(
                        voice => voice.lang.includes('en-US') || voice.lang.includes('en-GB')
                    ) || availableSynthesisVoices[0];
                    setSpeechVoice(defaultVoice);
                }
            };

            // Chrome loads voices asynchronously
            if (window.speechSynthesis.onvoiceschanged !== undefined) {
                window.speechSynthesis.onvoiceschanged = loadVoices;
            }

            loadVoices(); // Initial load for Firefox/Safari
        }
    }, []);

    // Cancel speech synthesis when component unmounts
    useEffect(() => {
        return () => {
            if ('speechSynthesis' in window) {
                window.speechSynthesis.cancel();
            }
        };
    }, []);

    // Cleanup audio resources
    useEffect(() => {
        return () => {
            stopRecording();
            if (audioContextRef.current) {
                // Check if the AudioContext is not already closed before closing it
                if (audioContextRef.current.state !== 'closed') {
                    audioContextRef.current.close().catch(err => {
                        console.warn('Error closing AudioContext:', err);
                    });
                }
            }
        };
    }, []);

    // Mode configurations
    const modes = {
        'chat': {
            label: 'General',
            icon: <MessageIcon fontSize="small"/>,
            color: '#6c757d',
            endpoint: '/api/ai/chat',
            timeout: 30000
        },
        'multimodal': {
            label: 'Image Analysis',
            icon: <ImageIcon fontSize="small"/>,
            color: '#4caf50',
            endpoint: '/api/ai/multimodal/analyze',
            timeout: 45000
        },
        'document': {
            label: 'Document Verification',
            icon: <AssignmentIcon fontSize="small"/>,
            color: '#ff9800',
            endpoint: '/api/ai/student/verify-document',
            timeout: 45000
        },
        'assignment': {
            label: 'Assignment Grading',
            icon: <SchoolIcon fontSize="small"/>,
            color: '#e91e63',
            endpoint: '/api/ai/assignment/grade',
            timeout: 45000
        },
        'library': {
            label: 'Book Catalog',
            icon: <LibraryIcon fontSize="small"/>,
            color: '#9c27b0',
            endpoint: '/api/ai/library/book-catalog',
            timeout: 45000
        },
        'fees': {
            label: 'Fee Receipt',
            icon: <ReceiptIcon fontSize="small"/>,
            color: '#2196f3',
            endpoint: '/api/ai/fees/receipt-analysis',
            timeout: 45000
        },
        'facility': {
            label: 'Facility Inspection',
            icon: <FacilityIcon fontSize="small"/>,
            color: '#607d8b',
            endpoint: '/api/ai/infrastructure/facility-inspection',
            timeout: 45000
        }
    };

    // Audio recording functions
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                }
            });

            streamRef.current = stream;
            audioChunksRef.current = [];

            // Set up audio analysis
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            analyserRef.current = audioContextRef.current.createAnalyser();
            const source = audioContextRef.current.createMediaStreamSource(stream);
            source.connect(analyserRef.current);
            analyserRef.current.fftSize = 256;

            // Set up MediaRecorder
            mediaRecorderRef.current = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus'
            });

            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = handleRecordingStop;

            mediaRecorderRef.current.start(100); // Collect data every 100ms
            setIsRecording(true);
            setShowRecordingDialog(true);
            setRecordingTime(0);

            // Start timer and audio level monitoring
            recordingIntervalRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
                updateAudioLevel();
            }, 1000);

        } catch (error) {
            console.error('Error starting recording:', error);
            setError('Could not access microphone. Please check permissions.');
        }
    };

    const updateAudioLevel = () => {
        if (analyserRef.current) {
            const bufferLength = analyserRef.current.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);
            analyserRef.current.getByteFrequencyData(dataArray);

            const average = dataArray.reduce((a, b) => a + b) / bufferLength;
            setAudioLevel(average / 255);
        }
    };

    const pauseRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            if (isPaused) {
                mediaRecorderRef.current.resume();
                setIsPaused(false);
            } else {
                mediaRecorderRef.current.pause();
                setIsPaused(true);
            }
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
        }

        if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
        }

        setIsRecording(false);
        setIsPaused(false);
        setShowRecordingDialog(false);
        setAudioLevel(0);
    };

    const handleRecordingStop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {type: 'audio/webm'});
        await transcribeAudio(audioBlob);
    };

    const transcribeAudio = async (audioBlob) => {
        setIsTranscribing(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('file', audioBlob, 'recording.webm');

            const response = await api.post('/api/ai/audio/transcribe', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 30000
            });

            // Extract the response content from the API response object
            const responseData = response.data;

            // Check if the request was successful
            if (responseData.success) {
                const transcription = responseData.response;

                if (transcription && transcription.trim()) {
                    setInputMessage(prev => prev + (prev ? ' ' : '') + transcription);
                    inputRef.current?.focus();
                } else {
                    setError('No speech detected in the recording');
                }
            } else {
                // Handle unsuccessful response
                setError(responseData.error || 'Failed to transcribe audio. Please try again.');
            }
        } catch (error) {
            console.error('Transcription error:', error);
            setError('Failed to transcribe audio. Please try again.');
        } finally {
            setIsTranscribing(false);
        }
    };

    const handleFileUpload = async (file) => {
        if (!file) return;

        // Check file type
        const allowedTypes = ['audio/wav', 'audio/mp3', 'audio/m4a', 'audio/webm', 'audio/ogg'];
        if (!allowedTypes.includes(file.type)) {
            setError('Please upload an audio file (WAV, MP3, M4A, WebM, OGG)');
            return;
        }

        // Check file size (max 25MB)
        if (file.size > 25 * 1024 * 1024) {
            setError('File size too large. Please upload a file smaller than 25MB.');
            return;
        }

        await transcribeAudio(file);
        setShowFileUpload(false);
    };

    // Image handling functions
    const handleImageSelect = (file) => {
        if (!file) return;

        // Check file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
        if (!allowedTypes.includes(file.type)) {
            setError('Please upload an image file (JPEG, PNG, GIF, WebP, BMP)');
            return;
        }

        // Check file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            setError('Image file size exceeds maximum limit of 10MB');
            return;
        }

        setSelectedImage(file);
        const imageUrl = URL.createObjectURL(file);
        setSelectedImageUrl(imageUrl);
    };

    const handleImageDrop = useCallback((e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0 && files[0].type.startsWith('image/')) {
            handleImageSelect(files[0]);
        }
    }, []);

    const handleImageDragOver = useCallback((e) => {
        e.preventDefault();
    }, []);

    const clearSelectedImage = () => {
        if (selectedImageUrl) {
            URL.revokeObjectURL(selectedImageUrl);
        }
        setSelectedImage(null);
        setSelectedImageUrl('');
        setImageAnalysisPrompt('');
    };

    // Handle file drop for audio uploads
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0 && files[0].type.startsWith('audio/')) {
            handleFileUpload(files[0]);
        }
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
    }, []);

    const handleImageAnalysis = async () => {
        if (!selectedImage) {
            setError('Please select an image to analyze');
            return;
        }

        setImageUploading(true);
        setError(null);
        setShowImageUpload(false);

        try {
            // Create form data for the multipart request
            const formData = new FormData();
            formData.append('file', selectedImage);

            // If there's a text prompt, include it
            if (imageAnalysisPrompt || inputMessage) {
                const textPrompt = imageAnalysisPrompt || inputMessage;
                formData.append('message', textPrompt);

                // Add user message to chat
                const userMessage = {
                    id: Date.now(),
                    text: textPrompt,
                    sender: 'user',
                    timestamp: new Date(),
                    mode: imageUploadMode,
                    hasImage: true,
                    imageUrl: selectedImageUrl
                };

                setMessages(prev => [...prev, userMessage]);
            } else {
                // Generic message if no specific prompt
                const userMessage = {
                    id: Date.now(),
                    text: `[Image uploaded for ${modes[imageUploadMode].label}]`,
                    sender: 'user',
                    timestamp: new Date(),
                    mode: imageUploadMode,
                    hasImage: true,
                    imageUrl: selectedImageUrl
                };

                setMessages(prev => [...prev, userMessage]);
            }

            // Additional parameters for specialized endpoints
            if (imageUploadMode === 'document' && selectedSpecialization) {
                formData.append('documentType', selectedSpecialization);
            } else if (imageUploadMode === 'assignment' && selectedSpecialization) {
                formData.append('subject', selectedSpecialization);
                formData.append('rubric', 'Standard assessment rubric');
            } else if (imageUploadMode === 'library' && selectedSpecialization) {
                formData.append('action', selectedSpecialization);
            }

            setInputMessage('');
            setIsLoading(true);

            // Determine which endpoint to use based on the mode
            const endpoint = modes[imageUploadMode].endpoint;
            const timeout = modes[imageUploadMode].timeout;

            const response = await api.post(endpoint, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: timeout
            });

            // Process response
            const aiMessage = {
                id: Date.now() + 1,
                text: response.data.response || "I couldn't analyze the image. Please try again.",
                sender: 'ai',
                timestamp: new Date(),
                success: response.data.success,
                mode: imageUploadMode,
                referencesImage: true
            };

            setMessages(prev => [...prev, aiMessage]);

            if (!response.data.success && response.data.error) {
                setError(response.data.error);
            }

        } catch (err) {
            console.error('API Error during image analysis:', err);
            handleAPIError(err, imageUploadMode);
        } finally {
            setIsLoading(false);
            setImageUploading(false);
            clearSelectedImage();
        }
    };

    const handleSendMessage = async (messageText = null, mode = null) => {
        const textToSend = messageText || inputMessage;
        const currentMode = mode || selectedMode;

        if (!textToSend.trim()) {
            setError('Message cannot be empty');
            setTimeout(() => setError(null), 3000);
            return;
        }

        const userMessage = {
            id: Date.now(),
            text: textToSend,
            sender: 'user',
            timestamp: new Date(),
            mode: currentMode
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsLoading(true);
        setError(null);

        try {
            const modeConfig = modes[currentMode];
            const response = await api.post(modeConfig.endpoint, {
                message: textToSend,
                context: context.trim() || null
            }, {
                headers: {
                    'Content-Type': 'application/json',
                },
                timeout: modeConfig.timeout
            });

            const aiMessage = {
                id: Date.now() + 1,
                text: response.data.response || "I couldn't generate a response. Please try again.",
                sender: 'ai',
                timestamp: new Date(),
                success: response.data.success,
                mode: currentMode,
                modelUsed: response.data.modelUsed,
                responseTime: response.data.responseTime
            };

            setMessages(prev => [...prev, aiMessage]);

            if (!response.data.success && response.data.error) {
                setError(response.data.error);
            }

        } catch (err) {
            console.error('API Error:', err);
            handleAPIError(err, currentMode);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAPIError = (err, mode) => {
        let errorMessage = 'Failed to get AI response';

        if (err.response) {
            errorMessage = err.response.data?.error || `Server error: ${err.response.status}`;
        } else if (err.request) {
            errorMessage = 'No response from server. Please check your connection.';
        } else if (err.code === 'ECONNABORTED') {
            errorMessage = `Request timed out. ${mode === 'quick-chat' ? 'Try regular chat for complex queries.' : 'The AI is processing your request, please try again.'}`;
        }

        setError(errorMessage);

        const errorMsg = {
            id: Date.now() + 1,
            text: errorMessage,
            sender: 'system',
            timestamp: new Date(),
            isError: true
        };

        setMessages(prev => [...prev, errorMsg]);
    };

    const regenerateResponse = async (messageId) => {
        const messageIndex = messages.findIndex(m => m.id === messageId);
        if (messageIndex > 0) {
            const userMessage = messages[messageIndex - 1];
            setMessages(prev => prev.filter(m => m.id !== messageId));
            await handleSendMessage(userMessage.text, userMessage.mode);
        }
    };

    const copyMessage = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy text:', err);
        }
    };

    // Text-to-speech functions
    const speakText = (text) => {
        if (!('speechSynthesis' in window)) {
            setError('Text-to-speech is not supported in your browser');
            return;
        }

        // Log to help with debugging
        console.log('Starting speech synthesis with text length:', text.length);
        console.log('Available voices:', availableVoices.length);
        console.log('Selected voice:', speechVoice ? speechVoice.name : 'None');

        // Stop any ongoing speech
        stopSpeaking();

        // For very long text, split it to avoid Chrome's bug
        // Chrome stops speaking after about 15 seconds
        const chunks = [];
        if (text.length > 200) {
            // Split by sentence ends or commas if text is long
            const sentences = text.match(/[^.!?;]+[.!?;]+|\s*[.!?;]+\s*/g) || [];
            let currentChunk = '';

            sentences.forEach(sentence => {
                if (currentChunk.length + sentence.length < 200) {
                    currentChunk += sentence;
                } else {
                    if (currentChunk) chunks.push(currentChunk);
                    currentChunk = sentence;
                }
            });

            if (currentChunk) chunks.push(currentChunk);
        } else {
            chunks.push(text);
        }

        // Create a new speech synthesis utterance
        const speakChunks = (index = 0) => {
            if (index >= chunks.length) return;

            const utterance = new SpeechSynthesisUtterance(chunks[index]);

            // Apply settings
            if (speechVoice) {
                utterance.voice = speechVoice;
            } else if (availableVoices.length > 0) {
                // Fallback to first available voice if none selected
                utterance.voice = availableVoices[0];
            }

            utterance.rate = speechRate;
            utterance.pitch = speechPitch;

            // Set up events
            utterance.onstart = () => setIsSpeaking(true);

            utterance.onend = () => {
                // Move to next chunk if available
                if (index < chunks.length - 1) {
                    speakChunks(index + 1);
                } else {
                    setIsSpeaking(false);
                }
            };

            utterance.onerror = (event) => {
                console.error('Speech synthesis error:', event);
                setIsSpeaking(false);
                setError('Error during speech synthesis: ' + (event.error || 'unknown error'));
            };

            // Fix for Chrome and Safari - need a longer delay before speaking
            // This helps with browser restrictions
            setTimeout(() => {
                try {
                    window.speechSynthesis.speak(utterance);

                    // Chrome and Safari bug fix - it sometimes pauses after 15 seconds
                    // This keeps the synthesis active
                    if (index === 0) {
                        const keepAlive = setInterval(() => {
                            if (!isSpeaking) {
                                clearInterval(keepAlive);
                                return;
                            }
                            // This forces the speech synthesis to stay active
                            window.speechSynthesis.pause();
                            window.speechSynthesis.resume();
                        }, 10000);
                    }
                } catch (e) {
                    console.error('Failed to start speech:', e);
                    setError('Failed to start speech: ' + e.message);
                    setIsSpeaking(false);
                }
            }, 150); // Increased delay for more reliability
        };

        // Start speaking the first chunk
        speakChunks();
    };

    const stopSpeaking = () => {
        if ('speechSynthesis' in window) {
            console.log('Stopping any ongoing speech');
            window.speechSynthesis.cancel();
            setIsSpeaking(false);
        }
    };

    // Auto-speak the latest AI message when it arrives
    useEffect(() => {
        const latestMessage = messages[messages.length - 1];
        if (latestMessage && latestMessage.sender === 'ai' && !latestMessage.isError && !isLoading) {
            speakText(latestMessage.text);
        }
    }, [messages, isLoading]);

    const clearChat = () => {
        setMessages([]);
        setError(null);
        setContext('');
        inputRef.current?.focus();
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const formatTime = (timestamp) => {
        return new Intl.DateTimeFormat('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        }).format(timestamp);
    };

    const formatRecordingTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Toggle dark mode function
    const toggleDarkMode = () => {
        setDarkMode(prev => !prev);
    };

    // Create app theme based on dark mode state
    const appTheme = useMemo(() => {
        return createTheme({
            ...theme,
            palette: {
                ...theme.palette,
                mode: darkMode ? 'dark' : 'light',
                background: {
                    default: darkMode ? '#121212' : '#f8f9fa',
                    paper: darkMode ? '#1e1e1e' : '#ffffff',
                },
                text: {
                    primary: darkMode ? '#e0e0e0' : '#2e3338',
                    secondary: darkMode ? '#a0a0a0' : '#4e5058',
                },
            },
        });
    }, [darkMode]);

    return (
        <ThemeProvider theme={appTheme}>
            <CssBaseline/>
            <ChatContainer sx={{
                backgroundColor: 'background.default',
            }}>
                <Box sx={{display: 'flex', height: '100vh'}}>
                    <MainContent>
                        {/* Header with theme toggle and settings */}
                        <Box sx={{
                            p: 2,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <ModeSelector>
                                {Object.entries(modes).map(([key, mode]) => (
                                    <ModeChip
                                        key={key}
                                        icon={mode.icon}
                                        label={mode.label}
                                        selected={selectedMode === key}
                                        onClick={() => setSelectedMode(key)}
                                        clickable
                                    />
                                ))}
                            </ModeSelector>

                            <Box>
                                <Tooltip title="Toggle Dark Mode">
                                    <IconButton
                                        onClick={toggleDarkMode}
                                        sx={{mr: 1}}
                                    >
                                        {darkMode ? <LightModeIcon/> : <DarkModeIcon/>}
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Speech Settings">
                                    <IconButton
                                        onClick={() => setShowSpeechSettings(true)}
                                        sx={{mr: 1}}
                                    >
                                        <SettingsIcon/>
                                    </IconButton>
                                </Tooltip>

                                <Button
                                    onClick={clearChat}
                                    variant="outlined"
                                    size="small"
                                    sx={{borderRadius: '8px'}}
                                >
                                    Clear
                                </Button>
                            </Box>
                        </Box>

                        {/* Error Display */}
                        {error && (
                            <Box sx={{p: 2}}>
                                <Alert
                                    severity="error"
                                    onClose={() => setError(null)}
                                    sx={{borderRadius: '12px'}}
                                >
                                    {error}
                                </Alert>
                            </Box>
                        )}

                        {/* Messages */}
                        <MessagesContainer>
                            {messages.length === 0 ? (
                                <WelcomeCard elevation={0}>
                                    <WelcomeIcon>
                                        <BotIcon sx={{fontSize: 40, color: 'white'}}/>
                                    </WelcomeIcon>
                                    <Typography variant="h4" gutterBottom sx={{fontWeight: 700, mb: 2}}>
                                        Welcome to SchoolIQ AI
                                    </Typography>
                                    <Typography variant="body1" sx={{color: 'text.secondary', mb: 4, lineHeight: 1.6}}>
                                        Your intelligent study companion is ready to help. You can type, record audio,
                                        or upload audio files!
                                    </Typography>
                                </WelcomeCard>
                            ) : (
                                <>
                                    {messages.map((message) => (
                                        <MessageBubble key={message.id} sender={message.sender}>
                                            <MessageHeader>
                                                <ModernAvatar variant={message.sender}>
                                                    {message.sender === 'user' ? (
                                                        <PersonIcon fontSize="small"/>
                                                    ) : message.isError ? (
                                                        <CodeIcon fontSize="small"/>
                                                    ) : (
                                                        <BotIcon fontSize="small"/>
                                                    )}
                                                </ModernAvatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {message.sender === 'user' ? 'You' : message.isError ? 'System' : 'SchoolIQ AI'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatTime(message.timestamp)}
                                                        {message.mode && ` • ${modes[message.mode]?.label}`}
                                                    </Typography>
                                                </Box>
                                            </MessageHeader>

                                            <MessageContent sender={message.sender}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        lineHeight: 1.6
                                                    }}
                                                >
                                                    {message.text}
                                                </Typography>
                                            </MessageContent>

                                            {message.sender === 'ai' && !message.isError && (
                                                <MessageActions className="message-actions">
                                                    <ActionChip
                                                        size="small"
                                                        icon={<CopyIcon fontSize="inherit"/>}
                                                        label="Copy"
                                                        onClick={() => copyMessage(message.text)}
                                                        clickable
                                                    />
                                                    <ActionChip
                                                        size="small"
                                                        icon={isSpeaking ? <MuteIcon fontSize="inherit"/> :
                                                            <SpeakIcon fontSize="inherit"/>}
                                                        label={isSpeaking ? "Stop" : "Speak"}
                                                        onClick={() => isSpeaking ? stopSpeaking() : speakText(message.text)}
                                                        clickable
                                                    />
                                                    <ActionChip
                                                        size="small"
                                                        icon={<ThumbUpIcon fontSize="inherit"/>}
                                                        label="Good"
                                                        clickable
                                                    />
                                                    <ActionChip
                                                        size="small"
                                                        icon={<ThumbDownIcon fontSize="inherit"/>}
                                                        label="Poor"
                                                        clickable
                                                    />
                                                    <ActionChip
                                                        size="small"
                                                        icon={<RegenerateIcon fontSize="inherit"/>}
                                                        label="Retry"
                                                        onClick={() => regenerateResponse(message.id)}
                                                        clickable
                                                    />
                                                </MessageActions>
                                            )}
                                        </MessageBubble>
                                    ))}

                                    {(isLoading || isTranscribing) && (
                                        <MessageBubble sender="ai">
                                            <MessageHeader>
                                                <ModernAvatar variant="ai">
                                                    <BotIcon fontSize="small"/>
                                                </ModernAvatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        SchoolIQ AI
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {isTranscribing ? 'Transcribing...' : 'Thinking...'}
                                                    </Typography>
                                                </Box>
                                            </MessageHeader>

                                            <MessageContent sender="ai">
                                                <TypingIndicator>
                                                    <Box className="typing-dot"/>
                                                    <Box className="typing-dot"/>
                                                    <Box className="typing-dot"/>
                                                    <Typography variant="body2" sx={{ml: 1}}>
                                                        {isTranscribing ? 'Converting speech to text...' : `${modes[selectedMode].label} processing...`}
                                                    </Typography>
                                                </TypingIndicator>
                                            </MessageContent>
                                        </MessageBubble>
                                    )}
                                </>
                            )}
                            <div ref={messagesEndRef}/>
                        </MessagesContainer>

                        {/* Input */}
                        <InputContainer>
                            <InputWrapper>
                                <StyledTextField
                                    ref={inputRef}
                                    fullWidth
                                    multiline
                                    maxRows={4}
                                    placeholder={`Type your message or use voice... (${modes[selectedMode].label} mode)`}
                                    value={inputMessage}
                                    onChange={(e) => setInputMessage(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    disabled={isLoading || isTranscribing}
                                />
                                <ButtonContainer>
                                    <ActionButton
                                        variant="secondary"
                                        onClick={clearChat}
                                        disabled={isLoading || isTranscribing}
                                        title="AI Tools"
                                    >
                                        <MoreIcon fontSize="small"/>
                                    </ActionButton>
                                    <ActionButton
                                        variant="secondary"
                                        onClick={startRecording}
                                        disabled={isLoading || isTranscribing || isRecording}
                                        title="Voice Input"
                                    >
                                        <MicIcon fontSize="small"/>
                                    </ActionButton>
                                    <ActionButton
                                        variant="secondary"
                                        onClick={() => setShowFileUpload(true)}
                                        disabled={isLoading || isTranscribing}
                                        title="Upload Audio"
                                    >
                                        <UploadIcon fontSize="small"/>
                                    </ActionButton>
                                    <ActionButton
                                        variant="secondary"
                                        onClick={() => setShowImageUpload(true)}
                                        disabled={isLoading || isTranscribing}
                                        title="Upload Image"
                                    >
                                        <ImageIcon fontSize="small"/>
                                    </ActionButton>
                                    <ActionButton
                                        variant="primary"
                                        onClick={() => handleSendMessage()}
                                        disabled={isLoading || isTranscribing || !inputMessage.trim()}
                                    >
                                        {isLoading ? (
                                            <CircularProgress size={20} color="inherit"/>
                                        ) : (
                                            <SendIcon fontSize="small"/>
                                        )}
                                    </ActionButton>
                                </ButtonContainer>
                            </InputWrapper>

                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    textAlign: 'center',
                                    mt: 2,
                                    color: 'text.secondary'
                                }}
                            >
                                SchoolIQ AI can make mistakes. Please verify important information.
                            </Typography>
                        </InputContainer>
                    </MainContent>
                </Box>

                {/* Recording Dialog */}
                <RecordingDialog
                    open={showRecordingDialog}
                    onClose={() => {
                    }}
                    disableEscapeKeyDown
                    disableBackdropClick
                >
                    <DialogTitle sx={{textAlign: 'center', pb: 2}}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1}}>
                            <MicIcon color="error"/>
                            <Typography variant="h6">Recording Audio</Typography>
                        </Box>
                        <Typography variant="h4" color="error" fontWeight="bold">
                            {formatRecordingTime(recordingTime)}
                        </Typography>
                        {isPaused && (
                            <Typography variant="caption" color="warning.main">
                                Recording Paused
                            </Typography>
                        )}
                    </DialogTitle>

                    <DialogContent>
                        <WaveformContainer>
                            {Array.from({length: 20}, (_, i) => (
                                <WaveBar
                                    key={i}
                                    amplitude={Math.random() * 0.5 + audioLevel * 0.5}
                                    style={{
                                        animationDelay: `${i * 0.1}s`,
                                        opacity: isPaused ? 0.3 : 1
                                    }}
                                />
                            ))}
                        </WaveformContainer>

                        <Typography variant="body2" align="center" color="text.secondary">
                            {isPaused ? 'Recording paused - click resume to continue' : 'Speak clearly into your microphone'}
                        </Typography>
                    </DialogContent>

                    <DialogActions sx={{justifyContent: 'center', pb: 3}}>
                        <Button
                            onClick={pauseRecording}
                            variant="outlined"
                            startIcon={isPaused ? <MicIcon/> : <MicOffIcon/>}
                            disabled={!isRecording}
                        >
                            {isPaused ? 'Resume' : 'Pause'}
                        </Button>
                        <Button
                            onClick={stopRecording}
                            variant="contained"
                            color="error"
                            startIcon={<StopIcon/>}
                        >
                            Stop & Transcribe
                        </Button>
                    </DialogActions>
                </RecordingDialog>

                {/* File Upload Dialog */}
                <Dialog
                    open={showFileUpload}
                    onClose={() => setShowFileUpload(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Upload Audio File</DialogTitle>
                    <DialogContent>
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{display: 'none'}}
                            accept="audio/*"
                            onChange={(e) => {
                                if (e.target.files[0]) {
                                    handleFileUpload(e.target.files[0]);
                                }
                            }}
                        />

                        <FileUploadArea
                            onClick={() => fileInputRef.current?.click()}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            elevation={0}
                        >
                            <UploadIcon sx={{fontSize: 48, color: 'text.secondary', mb: 2}}/>
                            <Typography variant="h6" gutterBottom>
                                Drop audio file here or click to browse
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Supported formats: WAV, MP3, M4A, WebM, OGG (max 25MB)
                            </Typography>
                        </FileUploadArea>

                        {isTranscribing && (
                            <Box sx={{mt: 2}}>
                                <LinearProgress/>
                                <Typography variant="caption" sx={{mt: 1, display: 'block', textAlign: 'center'}}>
                                    Transcribing audio...
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowFileUpload(false)}>Cancel</Button>
                    </DialogActions>
                </Dialog>

                {/* Image Upload Dialog */}
                <Dialog
                    open={showImageUpload}
                    onClose={() => setShowImageUpload(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Upload Image File</DialogTitle>
                    <DialogContent>
                        <input
                            type="file"
                            ref={imageInputRef}
                            style={{display: 'none'}}
                            accept="image/*"
                            onChange={(e) => {
                                if (e.target.files[0]) {
                                    handleImageSelect(e.target.files[0]);
                                }
                            }}
                        />

                        <FileUploadArea
                            onClick={() => imageInputRef.current?.click()}
                            onDrop={handleImageDrop}
                            onDragOver={handleImageDragOver}
                            elevation={0}
                        >
                            <ImageIcon sx={{fontSize: 48, color: 'text.secondary', mb: 2}}/>
                            <Typography variant="h6" gutterBottom>
                                Drop image file here or click to browse
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Supported formats: JPEG, PNG, GIF, WebP, BMP (max 10MB)
                            </Typography>
                        </FileUploadArea>

                        {selectedImageUrl && (
                            <Box sx={{mt: 2, textAlign: 'center'}}>
                                <Typography variant="body2" sx={{mb: 1}}>
                                    Selected Image:
                                </Typography>
                                <img
                                    src={selectedImageUrl}
                                    alt="Selected"
                                    style={{
                                        maxWidth: '100%',
                                        borderRadius: '8px',
                                        marginBottom: '8px',
                                        border: '1px solid #e9ecef',
                                    }}
                                />
                                <Button
                                    onClick={clearSelectedImage}
                                    variant="outlined"
                                    size="small"
                                    color="error"
                                    startIcon={<StopIcon fontSize="small"/>}
                                    sx={{borderRadius: '8px'}}
                                >
                                    Remove Image
                                </Button>
                            </Box>
                        )}

                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Analysis Prompt (optional)"
                            placeholder="Enter a prompt for image analysis..."
                            value={imageAnalysisPrompt}
                            onChange={(e) => setImageAnalysisPrompt(e.target.value)}
                            sx={{mt: 2}}
                        />

                        {imageUploading && (
                            <Box sx={{mt: 2}}>
                                <LinearProgress/>
                                <Typography variant="caption" sx={{mt: 1, display: 'block', textAlign: 'center'}}>
                                    Analyzing image...
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowImageUpload(false)}>Cancel</Button>
                        <Button
                            onClick={handleImageAnalysis}
                            variant="contained"
                            color="primary"
                            disabled={imageUploading || !selectedImage}
                        >
                            Analyze Image
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* AI Tools Menu */}
                <Menu
                    id="ai-tools-menu"
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    PaperProps={{
                        elevation: 3,
                        sx: {borderRadius: 2, width: 240}
                    }}
                >
                    <Typography sx={{px: 2, py: 1, fontWeight: 600, color: 'text.secondary'}}>
                        Image Analysis Tools
                    </Typography>

                    <MenuItem onClick={() => {
                        setImageUploadMode('multimodal');
                        setShowImageUpload(true);
                        setAnchorEl(null);
                    }}>
                        <ListItemIcon>
                            <ImageIcon fontSize="small" sx={{color: modes.multimodal.color}}/>
                        </ListItemIcon>
                        <ListItemText>General Image Analysis</ListItemText>
                    </MenuItem>

                    <MenuItem onClick={() => {
                        setImageUploadMode('document');
                        setSelectedSpecialization('Student ID Card');
                        setShowImageUpload(true);
                        setAnchorEl(null);
                    }}>
                        <ListItemIcon>
                            <AssignmentIcon fontSize="small" sx={{color: modes.document.color}}/>
                        </ListItemIcon>
                        <ListItemText>Verify Document</ListItemText>
                    </MenuItem>

                    <MenuItem onClick={() => {
                        setImageUploadMode('assignment');
                        setSelectedSpecialization('General');
                        setShowImageUpload(true);
                        setAnchorEl(null);
                    }}>
                        <ListItemIcon>
                            <SchoolIcon fontSize="small" sx={{color: modes.assignment.color}}/>
                        </ListItemIcon>
                        <ListItemText>Grade Assignment</ListItemText>
                    </MenuItem>

                    <Divider/>

                    <MenuItem onClick={() => {
                        setImageUploadMode('library');
                        setSelectedSpecialization('catalog');
                        setShowImageUpload(true);
                        setAnchorEl(null);
                    }}>
                        <ListItemIcon>
                            <LibraryIcon fontSize="small" sx={{color: modes.library.color}}/>
                        </ListItemIcon>
                        <ListItemText>Catalog Book</ListItemText>
                    </MenuItem>

                    <MenuItem onClick={() => {
                        setImageUploadMode('fees');
                        setSelectedSpecialization(null);
                        setShowImageUpload(true);
                        setAnchorEl(null);
                    }}>
                        <ListItemIcon>
                            <ReceiptIcon fontSize="small" sx={{color: modes.fees.color}}/>
                        </ListItemIcon>
                        <ListItemText>Analyze Fee Receipt</ListItemText>
                    </MenuItem>

                    <MenuItem onClick={() => {
                        setImageUploadMode('facility');
                        setSelectedSpecialization(null);
                        setShowImageUpload(true);
                        setAnchorEl(null);
                    }}>
                        <ListItemIcon>
                            <FacilityIcon fontSize="small" sx={{color: modes.facility.color}}/>
                        </ListItemIcon>
                        <ListItemText>Inspect Facility</ListItemText>
                    </MenuItem>
                </Menu>

                {/* Success Snackbars */}
                <Snackbar
                    open={copySuccess}
                    autoHideDuration={2000}
                    onClose={() => setCopySuccess(false)}
                    message="✅ Copied to clipboard!"
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                />
            </ChatContainer>
        </ThemeProvider>
    );
};

export default AIChat;

